import { useState, useMemo } from "react";
import { CustomModal, StaticSelect, FormError, showToast } from "../shared";
import { useTransferTeamOwnership } from "../../lib/react-query/teams";

const TransferOwnershipModal = ({ isOpen, onClose, teamId, members, currentUserId }) => {
  const { mutateAsync: transferOwnership, isLoading } = useTransferTeamOwnership();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const candidateOptions = useMemo(() => {
    if (!Array.isArray(members)) return [];
    return members
      .filter((m) => m?.user?.id !== currentUserId && m?.user?.id !== undefined)
      .map((m) => ({
        id: String(m.user.id),
        name: m.user.full_name || m.user.email || `User #${m.user.id}`,
      }));
  }, [members, currentUserId]);

  const handleInitiate = () => {
    if (!selectedUserId) {
      setError("Please select a member to transfer ownership to.");
      return;
    }
    setError(null);
    setConfirming(true);
  };

  const handleConfirm = async () => {
    try {
      setError(null);
      const payload = { user_id: Number(selectedUserId) };
      await transferOwnership({ teamId, payload });
      showToast("Ownership transferred successfully", "success", { position: "top-center" });
      setConfirming(false);
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to transfer ownership.";
      setError(msg);
      setConfirming(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirming(false);
    setError(null);
  };

  if (confirming) {
    const selectedName =
      candidateOptions.find((o) => o.id === selectedUserId)?.name || "this member";
    return (
      <CustomModal
        title="Confirm ownership transfer"
        open={isOpen}
        close={handleCancelConfirm}
        primaryButtonText="Yes, transfer ownership"
        primaryButtonClick={handleConfirm}
        primaryButtonColor="danger"
        primaryButtonLoading={isLoading}
        secondaryButtonText="Cancel"
        secondaryButtonClick={handleCancelConfirm}
        isDismissable={false}
      >
        <div className="flex flex-col gap-3 text-sm text-darkgrey">
          <p>
            You are about to transfer ownership of this team to <strong>{selectedName}</strong>.
          </p>
          <p className="text-grey">
            After transfer, they will have full control and you will become an admin. This action
            cannot be undone.
          </p>
          <FormError message={error} />
        </div>
      </CustomModal>
    );
  }

  return (
    <CustomModal
      title="Transfer ownership"
      open={isOpen}
      close={onClose}
      primaryButtonText="Continue"
      primaryButtonClick={handleInitiate}
      secondaryButtonText="Cancel"
      secondaryButtonClick={onClose}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-grey">
          Select a current team member to receive ownership.
        </p>
        <StaticSelect
          label="New owner"
          name="newOwner"
          options={candidateOptions}
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          placeholder="Choose a member..."
        />
        <FormError message={error} />
      </div>
    </CustomModal>
  );
};

export default TransferOwnershipModal;
